import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Sparkles, Sigma } from 'lucide-react';

import { DocumentMathTextBlock } from '../../components/MathTextBlock';
import { knowledgeService } from '../../services/knowledgeService';

export type TtqUpdateAbbrExprDialogProps = {
  open: boolean;
  particleId: string | null;
  initialAbbrExpr?: string | null;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (abbrExpr: string) => void;
};

/** 衍生颗粒 · 更新缩略式（精简 LaTeX）；portal 到 body，避免画布 transform 裁切 */
export function TtqUpdateAbbrExprDialog({
  open,
  particleId,
  initialAbbrExpr = '',
  submitting = false,
  error = null,
  onCancel,
  onConfirm,
}: TtqUpdateAbbrExprDialogProps) {
  const [draft, setDraft] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(String(initialAbbrExpr || '').trim());
    setAiError(null);
  }, [open, particleId, initialAbbrExpr]);

  if (!open || !particleId || typeof document === 'undefined') return null;

  const trimmed = draft.trim();
  const busy = submitting || aiBusy;
  const canSubmit = Boolean(trimmed) && !busy;
  const displayError = aiError || error;

  const handleAiSuggest = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      // 缩略式固定服务端 Flash，不传工作台 Pro/reasoner
      const result = await knowledgeService.suggestAbbrExpr(particleId);
      setDraft(String(result.abbrExpr || '').trim());
    } catch (suggestError) {
      setAiError(suggestError instanceof Error ? suggestError.message : 'AI 生成失败');
    } finally {
      setAiBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
      data-ttq-abbr-dialog=""
      onPointerDown={(event) => event.stopPropagation()}
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface p-4 shadow-xl"
        role="dialog"
        aria-modal
        aria-labelledby="ttq-abbr-expr-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-800">
            <Sigma size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p id="ttq-abbr-expr-title" className="text-sm font-semibold text-ink">
              更新缩略表达式
            </p>
            <p className="mt-1 text-xs text-muted">
              一枚精简 LaTeX（建议单行 $...$），应来自解题关键步骤，概括方法核而非答案数字。
            </p>
          </div>
        </div>

        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium text-muted">缩略式</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => { void handleAiSuggest(); }}
              className={
                'inline-flex items-center gap-1 rounded-md border border-teal-300/80 bg-teal-50/80 px-2 py-1 text-[11px] font-medium text-teal-900 '
                + (busy ? 'cursor-not-allowed opacity-60' : 'hover:bg-teal-100/80')
              }
            >
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {aiBusy ? '生成中…' : 'AI辅助更新'}
            </button>
          </div>

          <div className="mb-2 min-h-[3rem] rounded-lg border border-teal-200/80 bg-teal-50/60 px-3 py-2.5 text-[15px] leading-relaxed text-teal-950">
            <p className="mb-1 text-[10px] font-medium tracking-wide text-teal-800/70">所见即所得</p>
            {trimmed ? (
              <DocumentMathTextBlock text={trimmed} inline />
            ) : (
              <p className="text-[12px] text-teal-800/45">输入或由 AI 生成后，此处实时渲染公式</p>
            )}
          </div>

          <p className="mb-1 text-[10px] font-medium text-muted">LaTeX 源码</p>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={busy}
            rows={3}
            spellCheck={false}
            placeholder={'$n\\equiv 0\\pmod{2}$'}
            className="max-h-32 w-full resize-y overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-canvas/60 px-3 py-2 font-mono text-[12px] leading-relaxed text-ink outline-none focus:border-primary/40 disabled:opacity-60"
            autoFocus
          />
        </div>

        {displayError ? (
          <p className="mt-2 text-xs text-danger">{displayError}</p>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-lg border border-border py-2 text-sm text-muted hover:bg-black/[0.03] disabled:opacity-50"
            disabled={busy}
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            disabled={!canSubmit}
            onClick={() => onConfirm(trimmed)}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sigma size={14} />}
            {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
